import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { SkillItem } from '../types';

export function useSkillDetail(skillId: string | undefined) {
  const [skill, setSkill] = useState<SkillItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchSkill = useCallback(async () => {
    if (!skillId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('intern_skill_items')
        .select('*')
        .eq('id', skillId)
        .single();
      if (err) throw err;
      setSkill(data as SkillItem);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skill.');
    } finally {
      setLoading(false);
    }
  }, [skillId]);

  useEffect(() => {
    fetchSkill();
  }, [fetchSkill]);

  const updateStatus = useCallback(async (status: string) => {
    if (!skillId) return;
    const { error } = await supabase.rpc('set_intern_skill_progress', {
      p_skill_id: skillId,
      p_status: status,
    });
    if (!error) setSkill((s) => s ? { ...s, status: status as SkillItem['status'] } : s);
    return { error };
  }, [skillId]);

  // Autosave notes with debounce
  const updateNotes = useCallback((notes: string) => {
    setSkill((s) => s ? { ...s, notes } : s);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      if (!skillId) return;
      setSaving(true);
      await supabase.rpc('set_intern_skill_progress', {
        p_skill_id: skillId,
        p_status: skill?.status ?? 'suggested',
        p_notes: notes,
      });
      setSaving(false);
    }, 800);
  }, [skillId, skill?.status]);

  return { skill, loading, error, saving, updateStatus, updateNotes, refresh: fetchSkill };
}
